import { Compass, Globe2, Pencil, Smartphone, Sparkles, Users, Wifi } from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";

export function Cad() {
  return (
    <section id="cad" className="border-b border-border/60 bg-card/30">
      <div className="container py-20 md:py-28">
        <SectionHeading
          eyebrow="RADGIS CAD"
          title={
            <>
              Saha ve ofis için <span className="gradient-text">tek CAD ortamı</span>.
            </>
          }
          description="Aynı veri modeli, aynı yetkiler, aynı veri. Web tarayıcısında masaüstü hassasiyetinde çizim, tablet ve telefonda saha için optimize edilmiş yüksek performanslı çizim altyapısı."
        />

        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          <div className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-soft">
            <div className="flex items-center justify-between border-b border-border/60 bg-muted/40 px-4 py-3 text-sm">
              <div className="flex items-center gap-2 font-medium">
                <Pencil className="h-4 w-4" /> RADGIS CAD — Web
              </div>
              <div className="text-xs text-muted-foreground">
                Akarsu · Bölge 03 · Altyapı Projesi
              </div>
            </div>
            <div className="relative aspect-[4/3] grid-bg">
              <svg viewBox="0 0 400 300" className="h-full w-full">
                <path
                  d="M 20 250 Q 100 240 160 200 T 320 120 L 380 60"
                  stroke="hsl(var(--project-master))"
                  strokeWidth="2.5"
                  fill="none"
                />
                <path
                  d="M 60 270 Q 120 230 180 220 T 320 180"
                  stroke="hsl(var(--project-sub))"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="4 4"
                />
                {[
                  [60, 250],
                  [160, 200],
                  [240, 160],
                  [320, 120],
                ].map(([x, y]) => (
                  <circle
                    key={`${x}-${y}`}
                    cx={x}
                    cy={y}
                    r={5}
                    fill="hsl(var(--project-master))"
                    stroke="hsl(var(--background))"
                    strokeWidth={2}
                  />
                ))}
                {[
                  [120, 240],
                  [200, 215],
                  [280, 180],
                ].map(([x, y]) => (
                  <rect
                    key={`${x}-${y}`}
                    x={(x as number) - 4}
                    y={(y as number) - 4}
                    width={8}
                    height={8}
                    fill="hsl(var(--project-sub))"
                    stroke="hsl(var(--background))"
                    strokeWidth={1.5}
                  />
                ))}
              </svg>
              <div className="absolute left-3 top-3 rounded-md border border-border/60 bg-background/80 px-2 py-1 text-[11px] backdrop-blur">
                Vana · Boru · Branşman
              </div>
              <div className="absolute right-3 bottom-3 rounded-md border border-border/60 bg-background/80 px-2 py-1 text-[11px] backdrop-blur">
                1:500 · WGS84
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {[
              {
                icon: Globe2,
                title: "CBS ve harita mühendisliğine özel rafineler",
                desc: "Koordinat sistemi dönüşümleri (UTM, ITRF96, ED50, WGS84), uydu ve ortofoto altlık görüntüleri, ölçek hassasiyeti ve datum uyumu — sektöre özgü ihtiyaçlar standart geliyor.",
              },
              {
                icon: Pencil,
                title: "CAD hassasiyeti",
                desc: "Snap, ortogonal, polyline / polygon kapatma, öznitelik formu — kurumsal CAD standartlarında çizim deneyimi. Mevcut Altyapı tarafında tam versiyon geçmişi ile her değişiklik izlenebilir.",
              },
              {
                icon: Smartphone,
                title: "Saha mobil uygulaması",
                desc: "Tablet ve telefonda yerel uygulama performansı. Kullanıcı çevrimdışı çalışsa bile çizim ve öznitelik girişi sürer; bağlantı geri geldiğinde Altyapı Projesi'ne otomatik senkronizasyon.",
              },
              {
                icon: Compass,
                title: "GNSS entegrasyonu",
                desc: "Harici GNSS alıcılarıyla santimetre seviyesinde konum girişi. Saha noktalarını anlık olarak doğru koordinatla kaydedin.",
              },
              {
                icon: Wifi,
                title: "Çevrimdışı-öncelikli çalışma",
                desc: "Saha ekibi tünelde, mahzende, dağ başında bağlantısız çalışsa bile uygulama bloke olmaz. Çizimler ve öznitelik girişleri cihazda saklanır; bağlantı geri geldiğinde değişiklikler arka planda otomatik senkronize olur.",
              },
              {
                icon: Users,
                title: "Eş zamanlı işbirliği",
                desc: "Aynı proje üzerinde aynı anda çalışan birden fazla kullanıcının düzenlemeleri otomatik birleşir — kimsenin işi diğerinin üstüne yazılmaz, manuel çakışma çözümü gerekmez. (Bu, Altyapı Projesi'nden Mevcut Altyapı'ya revizyon teslimindeki kurum onayından farklı, ondan bağımsız çalışan bir mekanizmadır.)",
              },
            ].map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="flex gap-4 rounded-xl border border-border/60 bg-background/70 p-5"
              >
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-muted">
                  <Icon className="h-5 w-5 text-foreground/80" />
                </div>
                <div>
                  <div className="font-medium">{title}</div>
                  <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 overflow-hidden rounded-2xl border border-border/60 bg-card">
          <div className="p-6 md:p-9">
            <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              <Sparkles className="h-3 w-3" />
              Sadece CBS değil
            </div>
            <h3 className="mt-4 text-balance text-2xl font-semibold tracking-tight md:text-3xl">
              Aynı CAD, sektörden bağımsız çizim aracı olarak da çalışır.
            </h3>
            <p className="mt-3 max-w-3xl text-pretty text-base text-muted-foreground">
              Koordinat dönüşümleri, uydu altlığı ve mühendislik hassasiyetleri RADGIS CAD'i altyapı
              işlerinde güçlü kılar — ama bu, onu diğer kullanım senaryolarına kapatmaz. Kurumsal
              veri modeline ihtiyaç duymayan her çizim, etüt veya kroki çalışmasını{" "}
              <span className="font-medium text-foreground">serbest proje</span> olarak açarsınız ve
              aynı güçlü CAD ortamını dilediğiniz gibi kullanırsınız.
            </p>

            <ul className="mt-5 grid gap-2 text-sm text-muted-foreground md:grid-cols-3 md:gap-4">
              {[
                <>
                  <span className="font-semibold text-foreground">Pro</span> workspace açar,
                  ekibiyle birlikte serbest projeler üzerinde çalışır.
                </>,
                <>
                  <span className="font-semibold text-foreground">Basic</span> serbest projelerini
                  Free / Basic / Pro fark etmeksizin paylaşır, birlikte çizim yapar.
                </>,
                <>
                  <span className="font-semibold text-foreground">Free</span> kendi serbest
                  projelerini açar, kullanır, başkalarının projelerine davet edildiğinde katılır.
                </>,
              ].map((node, i) => (
                <li
                  key={i}
                  className="flex gap-2 rounded-lg border border-border/50 bg-background/60 p-3"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  <span className="leading-relaxed">{node}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-6 border-t border-border/60 bg-primary/5 px-6 pb-12 pt-7 md:flex-row md:items-center md:gap-8 md:px-9 md:pb-14 md:pt-9">
            <div className="min-w-0 flex-1">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Free Tier
              </span>
              <div className="mt-3 text-2xl font-semibold tracking-tight text-foreground md:text-[1.75rem]">
                Tamamen ücretsiz.
              </div>
              <p className="mt-1.5 max-w-xl text-sm text-muted-foreground">
                Kart bilgisi yok, deneme süresi yok, sürpriz fatura yok. CAD'i bireysel veya saha
                kullanımı için açın.
              </p>
            </div>
            <a
              href="#fiyat"
              className="inline-flex w-fit shrink-0 items-center gap-1.5 self-start rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 md:ml-auto md:self-center"
            >
              Plan karşılaştırması →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
